import { useEffect, useState } from 'react'
import { Typography } from 'antd'
import { productApi } from 'api/product'
import { SaveOutlined } from '@ant-design/icons'
import {
  AiEnhancementOptions,
  EnhancementDescriptionResponse,
  EnhancementImageResponse,
  EnhancementResponse,
  ICharacteristic
} from 'models/product'
import { useNavigate } from 'react-router-dom'
import { showServerError } from 'shared/utils/showServerError'
import arrowBack from 'assets/icons/arrow-back.svg'
import { OrangeButton } from 'components/ui'
import { showMessage } from 'shared/utils/showMessage'
import { AiProductCharacteristics } from '../AiProductCharacteristics/AiProductCharacteristics'
import { AiProductImage } from '../AiProductImage/AiProductImage'
import { AiProductDescription } from '../AiProductDescription/AiProductDescription'
import styles from './AiProduct.module.scss'

interface AiProductProps {
  productId: string
  createData?: AiEnhancementOptions
  type: 'create' | 'edit'
}

export const AiProduct: React.FC<AiProductProps> = ({
  productId,
  createData,
  type
}) => {
  const [saveLoading, setSaveLoading] = useState(false)

  const [selectedImage, setSelectedImage] =
    useState<EnhancementImageResponse | null>(null)
  const [characteristics, setCharacteristics] = useState<ICharacteristic[]>([])
  const [selectedDescription, setSelectedDescription] =
    useState<EnhancementDescriptionResponse | null>(null)

  const [data, setData] = useState<EnhancementResponse | null>(null)
  const [itemId, setItemId] = useState('')

  useEffect(() => {
    if (type === 'edit') {
      const getAiProduct = async () => {
        try {
          const response = await productApi.getAiProduct(productId)
          setData(response.data)
          setItemId(response.data._id)
          if (response.data.ai_image_variants.length) {
            setSelectedImage(
              response.data.ai_image_variants[
                response.data.ai_image_variants.length - 1
              ]
            )
          }
          if (response.data.ai_characteristics_variants.length) {
            setCharacteristics(
              response.data.ai_characteristics_variants[
                response.data.ai_characteristics_variants.length - 1
              ].characteristics
            )
          }
          if (response.data.ai_descriptions_variants.length) {
            setSelectedDescription(
              response.data.ai_descriptions_variants[
                response.data.ai_descriptions_variants.length - 1
              ]
            )
          }
        } catch (e) {
          showServerError(e)
        }
      }
      getAiProduct()
    }
  }, [type])

  useEffect(() => {
    if (createData?.selectedInitialImage) {
      setSelectedImage(createData.selectedInitialImage)
    }
  }, [createData?.selectedInitialImage])

  const handleSave = async () => {
    try {
      setSaveLoading(true)
      await productApi.updateProduct({
        id: productId,
        generatedImage: selectedImage?.image,
        characteristic: characteristics.length ? characteristics : undefined,
        desc: selectedDescription?.new_desc
      })
      await productApi.deleteAiProducts([itemId])
      showMessage('success', 'Товар успешно сохранен', '')
    } catch (e) {
      showServerError(e)
    } finally {
      setSaveLoading(false)
    }
  }

  const navigate = useNavigate()
  return (
    <div style={{ paddingBottom: 80 }}>
      <div className={styles.header}>
        <button
          type='button'
          className='reset-button'
          onClick={() => navigate(-1)}
        >
          <img src={arrowBack} alt='Вернуться назад' />
        </button>
        <div className={styles.aiBadge}>
          <Typography.Text className={styles.aiBadgeText}>AI</Typography.Text>
        </div>
        <Typography.Text className={styles.headerDescription}>
          - Улучшение
        </Typography.Text>
        <OrangeButton
          size='large'
          className={styles.saveButton}
          loading={saveLoading}
          disabled={saveLoading}
          icon={<SaveOutlined />}
          onClick={handleSave}
        >
          Сохранить
        </OrangeButton>
      </div>
      <Typography.Title level={1} className={styles.productName}>
        {createData?.productName ?? data?.product?.name ?? ''}
      </Typography.Title>
      {(createData?.productImage ||
        createData?.characteristics ||
        !!data?.ai_image_variants?.length ||
        !!data?.ai_characteristics_variants?.length) && (
        <div className={styles.row}>
          {(createData?.productImage || !!data?.ai_image_variants?.length) && (
            <AiProductImage
              productId={productId}
              type={type}
              initialImages={data?.ai_image_variants}
              newImages={createData?.initialImages}
              generateType={createData?.imageEnhancementType}
              additionalPrompt={createData?.fullEnhancementImagePrompt}
              selectedImage={selectedImage}
              setSelectedImage={setSelectedImage}
              setItemId={setItemId}
            />
          )}
          {(createData?.characteristics ||
            !!data?.ai_characteristics_variants?.length) && (
            <AiProductCharacteristics
              productId={productId}
              type={type}
              characteristics={characteristics}
              setCharacteristics={setCharacteristics}
              setItemId={setItemId}
            />
          )}
        </div>
      )}
      {(createData?.improveDescription ||
        !!data?.ai_descriptions_variants?.length) && (
        <AiProductDescription
          productId={productId}
          type={type}
          initialDescriptions={data?.ai_descriptions_variants}
          additionalPrompt={createData?.improveDescriptionPrompt}
          selectedDescription={selectedDescription}
          setSelectedDescription={setSelectedDescription}
          setItemId={setItemId}
        />
      )}
    </div>
  )
}
